import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Design.css'; // CSS 파일 임포트

const API_KEY = process.env.REACT_APP_API_KEY;

// 학교 정보 가져오기
const fetchSchoolInfo = async (schoolName) => {
  try {
    const response = await axios.get(
      `https://open.neis.go.kr/hub/schoolInfo?KEY=${API_KEY}&Type=JSON&SCHUL_NM=${schoolName}`
    );
    return response.data;
  } catch (error) {
    console.error("학교 정보를 불러오지 못했습니다.", error);
    return null;
  }
};

// 학사일정 데이터 가져오기
const fetchScheduleData = async (schoolCode, eduCode, startDate, endDate) => {
  try {
    const response = await axios.get(
      `https://open.neis.go.kr/hub/SchoolSchedule?KEY=${API_KEY}&Type=JSON&ATPT_OFCDC_SC_CODE=${eduCode}&SD_SCHUL_CODE=${schoolCode}&AA_FROM_YMD=${startDate}&AA_TO_YMD=${endDate}&pSize=365`
    );
    return response.data;
  } catch (error) {
    console.error("학사일정 데이터를 불러오지 못했습니다.", error);
    return null;
  }
};

// 날짜 형식화
const formatDate = (dateString) => {
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  const formattedMonth = parseInt(month, 10);
  const formattedDay = parseInt(day, 10);

  const date = new Date(year, formattedMonth - 1, formattedDay);
  const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
  const weekday = weekdays[date.getDay()];

  return `${year}년 ${formattedMonth}월 ${formattedDay}일 (${weekday})`;
};

const SchoolSchedulePage = () => {
  const [schoolName, setSchoolName] = useState('');
  const [schoolList, setSchoolList] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(false);

  // 로컬 스토리지 동의 상태
  const [cookieConsent, setCookieConsent] = useState(false);

  const [excludeBreaks, setExcludeBreaks] = useState(false);  // 방학 제외
  const [excludeWeekends, setExcludeWeekends] = useState(false); // 토요휴업일 제외
  const [excludeHolidays, setExcludeHolidays] = useState(false); // 설날, 추석 제외

  // 쿠키 동의 확인 (로컬 스토리지 사용)
  useEffect(() => {
    const storedConsent = localStorage.getItem('cookieConsent');
    if (storedConsent === 'true') {
      setCookieConsent(true);
    }
  }, []);

  // 학교 저장 버튼 클릭 처리
  const handleSaveSchool = () => {
    if (cookieConsent) {
      // 로컬 스토리지에 학교 정보 저장
      if (selectedSchool) {
        localStorage.setItem('selectedSchool', JSON.stringify(selectedSchool));
        alert(`${selectedSchool.SCHUL_NM} 학교가 저장되었습니다.`);
      }
    } else {
      // 동의 창 띄우기
      const userConfirmed = window.confirm('이 사이트는 로컬 스토리지에 데이터를 저장하는 것에 동의하시겠습니까?');
      if (userConfirmed) {
        localStorage.setItem('cookieConsent', 'true');
        setCookieConsent(true);
        // 동의 후 학교 저장
        if (selectedSchool) {
          localStorage.setItem('selectedSchool', JSON.stringify(selectedSchool));
          alert(`${selectedSchool.SCHUL_NM} 학교가 저장되었습니다.`);
        }
      } else {
        alert('로컬 스토리지에 데이터를 저장하지 않으면 학교 정보를 저장할 수 없습니다.');
      }
    }
  };

  // 로컬 스토리지에서 저장된 학교 정보 가져오기
  useEffect(() => {
    const storedSchool = JSON.parse(localStorage.getItem('selectedSchool'));
    if (storedSchool) {
      setSelectedSchool(storedSchool);
    }
  }, []);

  // 학교 검색
  const searchSchool = async () => {
    if (schoolName === '') return;
    const schoolInfo = await fetchSchoolInfo(schoolName);
    if (schoolInfo && schoolInfo.schoolInfo && schoolInfo.schoolInfo[1].row) {
      setSchoolList(schoolInfo.schoolInfo[1].row);
    } else {
      setSchoolList([]);
    }
  };

  // 학사 일정 가져오기
  const fetchSchedules = async () => {
    if (!selectedSchool) return;
    setLoading(true);
    const { SD_SCHUL_CODE, ATPT_OFCDC_SC_CODE } = selectedSchool;

    const today = new Date();
    const startDate = today.toISOString().slice(0, 10).replace(/-/g, '');
    const endDate = `${parseInt(startDate.substring(0, 4)) + 1}0228`;  // 종료 날짜 계산

    const scheduleInfo = await fetchScheduleData(SD_SCHUL_CODE, ATPT_OFCDC_SC_CODE, startDate, endDate);
    if (scheduleInfo && scheduleInfo.SchoolSchedule && scheduleInfo.SchoolSchedule[1].row) {
      let filteredData = scheduleInfo.SchoolSchedule[1].row;

      // 방학 제외
      if (excludeBreaks) {
        filteredData = filteredData.filter((schedule) => 
          !schedule.EVENT_NM.includes('방학') && 
          !schedule.EVENT_NM.includes('여름방학') &&
          !schedule.EVENT_NM.includes('겨울방학') &&
          !schedule.EVENT_NM.includes('봄방학')
        );
      }

      // 토요휴업일 제외
      if (excludeWeekends) {
        filteredData = filteredData.filter((schedule) => 
          !schedule.EVENT_NM.includes('토요휴업일')
        );
      }

      // 설날, 추석 제외
      if (excludeHolidays) {
        filteredData = filteredData.filter((schedule) => 
          !schedule.EVENT_NM.includes('설날') && 
          !schedule.EVENT_NM.includes('추석')
        );
      }

      setScheduleData(filteredData);
    } else {
      setScheduleData([]);
    }
    setLoading(false);
  };

  // 선택된 학교 저장 (로컬 스토리지에 저장)
  const handleSelectSchool = (school) => {
    setSelectedSchool(school);
  };

  useEffect(() => {
    fetchSchedules();
  }, [selectedSchool, excludeBreaks, excludeWeekends, excludeHolidays]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchSchool();
    }
  };

  return (
    <div className="container">
      {/* 네비게이션 바 */}
      <div className="navbar">
        <div className="school-info">스쿨리오</div>
        <div className="menu">
          <a href="/school-meal">급식 메뉴</a>
          <a href="/schedule">학사일정</a>
          <a href="/timetable">시간표 보기</a>
        </div>
      </div>

      <h2 className="header">학교 학사일정 조회</h2>
      <div className="search-box">
        <input
          type="text"
          value={schoolName}
          onChange={(e) => setSchoolName(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="학교 이름을 입력하세요"
        />
        <button onClick={searchSchool}>🔍</button>
        {/* 학교 저장 버튼 */}
        {schoolList.length > 0 && (
          <button onClick={handleSaveSchool} disabled={!selectedSchool}>
            정보 저장
          </button>
        )}
      </div>

      {schoolList.length > 0 && (
        <ul className="school-list">
          {schoolList.map((school, index) => (
            <li key={index} onClick={() => handleSelectSchool(school)}>
              {school.SCHUL_NM}
            </li>
          ))}
        </ul>
      )}

      {selectedSchool && (
        <div className="schedule-info">
          <h3>{selectedSchool.SCHUL_NM}의 학사일정</h3>

          {/* 필터 체크박스 */}
          <div className="filter-options">
            <label>
              <input 
                type="checkbox" 
                checked={excludeBreaks} 
                onChange={() => setExcludeBreaks(!excludeBreaks)} 
              />
              방학 제외
            </label>
            <label>
              <input 
                type="checkbox" 
                checked={excludeWeekends} 
                onChange={() => setExcludeWeekends(!excludeWeekends)} 
              />
              토요휴업일 제외
            </label>
            <label>
              <input 
                type="checkbox" 
                checked={excludeHolidays} 
                onChange={() => setExcludeHolidays(!excludeHolidays)} 
              />
              설날, 추석 제외
            </label>
          </div>

          {loading ? (
            <div className="spinner"></div>
          ) : (
            <table className="schedule-table">
              <thead>
                <tr>
                  <th>일자</th>
                  <th>학사 일정</th>
                </tr>
              </thead>
              <tbody>
                {scheduleData.length > 0 ? (
                  scheduleData.map((schedule, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(schedule.AA_YMD)}</td>
                      <td>{schedule.EVENT_NM || '학사일정 정보가 없습니다.'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2">학사일정 정보가 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default SchoolSchedulePage;
